import React, { useEffect, useState } from 'react';
import { RecoilState, useRecoilState } from 'recoil';
import { Box, Grid } from '@mui/material';
import { ParameterSpace } from 'features/morphing/state/parameterAtoms';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { CustomNumberField } from 'shared/ui/CustomNumberField';

interface Props {
  parameterSpaceState: RecoilState<ParameterSpace>;
  label: string;
  labelXs?: number;
  fieldXs?: number;
  backgroundColor?: string;
}

export const ParameterSpaceForm: React.FC<Props> = ({
  parameterSpaceState,
  label,
  labelXs = 4,
  fieldXs = 3,
  backgroundColor = "#1e1e1e",
}) => {
  const [parameterSpace, setParameterSpace] = useRecoilState(parameterSpaceState);
  const [lower, setLower] = useState<string>(String(parameterSpace.lower));
  const [upper, setUpper] = useState<string>(String(parameterSpace.upper));

  useEffect(() => {
    setLower(String(parameterSpace.lower));
    setUpper(String(parameterSpace.upper));
  }, [parameterSpace]);

  const handleChange = (axis: 'lower' | 'upper') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Update the corresponding local state
    if (axis === 'lower') setLower(inputValue);
    if (axis === 'upper') setUpper(inputValue);

    // Parse the input value as a number
    const numericValue = parseInt(inputValue, 10);

    // If the input is a valid number, update the vector state
    if (!isNaN(numericValue)) {
      setParameterSpace(prevParameterSpace => ({
        ...prevParameterSpace,
        [axis]: numericValue,
      }));
    }
  };

  return (
    <Box component="div" sx={{ paddingTop: 1 }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={labelXs}>
          <CustomTypography>
            {label}
          </CustomTypography>
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="lower"
            type="number"
            fullWidth
            value={lower}
            onChange={handleChange('lower')}
            inputProps={{ min: -9999, max: parameterSpace.upper }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="upper"
            type="number"
            fullWidth
            value={upper}
            onChange={handleChange('upper')}
            inputProps={{ min: parameterSpace.lower, max: 9999 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};
